import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface SortPreference {
	lastSortedAs: string;
	column: string;
}

interface SortPreferences {
	users: SortPreference;
	groups: SortPreference;
	groupRules: SortPreference;
	groupUsers: SortPreference;
	groupPlugins: SortPreference;
	groupIdpGroups: SortPreference;
	roles: SortPreference;
	roleUsers: SortPreference;
	roleIdpGroups: SortPreference;
	workstations: SortPreference;
	plugins: SortPreference;
	pluginGroups: SortPreference;
	ruleLibrary: SortPreference;
	ruleTrash: SortPreference;
	collections: SortPreference;
	collectionEntries: SortPreference;
}

interface UserState {
	email: string | null;
	firstName: string | null;
	groups: any[];
	lastName: string | null;
	isCoAppSuperAdmin: boolean;
	organizationId: number | null;
	organizationName: string | null;
	organizationFailedSyncBehavior: string | null;
	organizationForceLogin: boolean | null;
	organizationIdentityProvider: string | null;
	organizationKillSwitch: boolean | null;
	organizationSsoAndScim: boolean | null;
	permissions: any[];
	phone: string | null;
	roles: any[];
	userId: number | null;
	descopeUserId: string | null;
	sortPreferences: SortPreferences;
}

const initialState: UserState = {
	email: null,
	firstName: null,
	groups: [],
	lastName: null,
	isCoAppSuperAdmin: false,
	organizationId: null,
	organizationName: null,
	organizationFailedSyncBehavior: null,
	organizationForceLogin: null,
	organizationIdentityProvider: null,
	organizationKillSwitch: null,
	organizationSsoAndScim: null,
	permissions: [],
	phone: null,
	roles: [],
	userId: null,
	descopeUserId: null,
	sortPreferences: {
		users: { lastSortedAs: "", column: "" },
		groups: { lastSortedAs: "", column: "" },
		groupRules: { lastSortedAs: "", column: "" },
		groupUsers: { lastSortedAs: "", column: "" },
		groupPlugins: { lastSortedAs: "", column: "" },
		groupIdpGroups: { lastSortedAs: "", column: "" },
		roles: { lastSortedAs: "", column: "" },
		roleUsers: { lastSortedAs: "", column: "" },
		roleIdpGroups: { lastSortedAs: "", column: "" },
		workstations: { lastSortedAs: "", column: "" },
		plugins: { lastSortedAs: "", column: "" },
		pluginGroups: { lastSortedAs: "", column: "" },
		ruleLibrary: { lastSortedAs: "", column: "" },
		ruleTrash: { lastSortedAs: "", column: "" },
		collections: { lastSortedAs: "", column: "" },
		collectionEntries: { lastSortedAs: "", column: "" },
	}
};

export const getUserInitialState = (): UserState => initialState;

export const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		setEmail: (state, action: PayloadAction<string | null>) => {
			state.email = action.payload;
		},
		setFirstName: (state, action: PayloadAction<string | null>) => {
			state.firstName = action.payload;
		},
		setGroups: (state, action: PayloadAction<any[]>) => {
			state.groups = action.payload;
		},
		setLastName: (state, action: PayloadAction<string | null>) => {
			state.lastName = action.payload;
		},
		setIsCoAppSuperAdmin: (state, action: PayloadAction<boolean>) => {
			state.isCoAppSuperAdmin = action.payload;
		},
		setOrgId: (state, action: PayloadAction<number | null>) => {
			state.organizationId = action.payload;
		},
		setOrganizationName: (state, action: PayloadAction<string | null>) => {
			state.organizationName = action.payload;
		},
		setOrganizationFailedSyncBehavior: (state, action: PayloadAction<string | null>) => {
			state.organizationFailedSyncBehavior = action.payload;
		},
		setOrganizationForceLogin: (state, action: PayloadAction<boolean | null>) => {
			state.organizationForceLogin = action.payload;
		},
		setOrganizationIdentityProvider: (state, action: PayloadAction<string | null>) => {
			state.organizationIdentityProvider = action.payload;
		},
		setOrganizationKillSwitch: (state, action: PayloadAction<boolean | null>) => {
			state.organizationKillSwitch = action.payload;
		},
		setOrganizationSsoAndScim: (state, action: PayloadAction<boolean | null>) => {
			state.organizationSsoAndScim = action.payload;
		},
		setPermissions: (state, action: PayloadAction<any[]>) => {
			state.permissions = action.payload;
		},
		setPhone: (state, action: PayloadAction<string | null>) => {
			state.phone = action.payload;
		},
		setRoles: (state, action: PayloadAction<any[]>) => {
			state.roles = action.payload;
		},
		setUserId: (state, action: PayloadAction<number | null>) => {
			state.userId = action.payload;
		},
		setSortPreferences: (state, action: PayloadAction<SortPreferences>) => {
			state.sortPreferences = action.payload;
		},
		setDescopeUserId: (state, action: PayloadAction<string | null>) => {
			state.descopeUserId = action.payload;
		}
	}
});

interface RootState {
	root: {
		user: UserState;
	};
}

export const selectEmail = (state: RootState) => state.root.user.email;
export const selectFirstName = (state: RootState) => state.root.user.firstName;
export const selectGroups = (state: RootState) => state.root.user.groups;
export const selectLastName = (state: RootState) => state.root.user.lastName;
export const selectIsCoAppSuperAdmin = (state: RootState) => state.root.user.isCoAppSuperAdmin;
export const selectOrganizationId = (state: RootState) => state.root.user.organizationId;
export const selectOrganizationName = (state: RootState) => state.root.user.organizationName;
export const selectOrganizationFailedSyncBehavior = (state: RootState) => state.root.user.organizationFailedSyncBehavior;
export const selectOrganizationForceLogin = (state: RootState) => state.root.user.organizationForceLogin;
export const selectOrganizationIdentityProvider = (state: RootState) => state.root.user.organizationIdentityProvider;
export const selectOrganizationKillSwitch = (state: RootState) => state.root.user.organizationKillSwitch;
export const selectOrganizationSsoAndScim = (state: RootState) => state.root.user.organizationSsoAndScim;
export const selectPermissions = (state: RootState) => state.root.user.permissions;
export const selectPhone = (state: RootState) => state.root.user.phone;
export const selectRoles = (state: RootState) => state.root.user.roles;
export const selectSortPreferences = (state: RootState) => state.root.user.sortPreferences;
export const selectUser = (state: RootState) => state.root.user;
export const selectUserId = (state: RootState) => state.root.user.userId;
export const selectDescopeUserId = (state: RootState) => state.root.user.descopeUserId;


export const {
	setEmail,
	setFirstName,
	setGroups,
	setLastName,
	setIsCoAppSuperAdmin,
	setPermissions,
	setPhone,
	setRoles,
	setUserId,
	setOrgId,
	setOrganizationName,
	setDescopeUserId,
	setOrganizationFailedSyncBehavior,
	setOrganizationForceLogin,
	setOrganizationIdentityProvider,
	setOrganizationKillSwitch,
	setOrganizationSsoAndScim,
	setSortPreferences
} = userSlice.actions;

export default userSlice.reducer;

export type { UserState };
